import client, {QueryOptions} from "@irati/utils/services/apollo"
import {
  Recognition,
  Poll,
  RecognitionSource,
  ReactionInput,
  RecognitionFeedComment,
  FeedEntityType,
  PaymentCurrencyType,
  WalletEntityType,
  GroupCelebrationList,
  GroupCelebration,
  GroupCelebrationStatus,
  UpdateGroupCelebrationInput
} from "@irati/types"
import PBService from "@irati/utils/services"
import {DataProxy} from "apollo-cache"
import authentication from "@irati/auth"
import rewardErrorHandler from "@irati/reward/services/errors/rewardErrorHandler"

import walletService from "@irati/wallet/graph/wallet"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import {
  MutationAddReactionArgs,
  MutationCreateGroupCelebrationArgs,
  MutationCreateGroupCelebrationMessageArgs,
  MutationCreateScheduledRecognitionArgs,
  MutationDeleteScheduledRecognitionArgs,
  MutationUpdateGroupCelebrationMessageArgs,
  MutationUpdateScheduledRecognitionArgs
} from "@irati/types/ops"
import {query as getScheduledRecognitionsQuery, scheduledRecognitionsQuery} from "../apollo/query/ScheduledRecognitions"
import scheduledRecognitionQuery, {scheduledRecognitionQueryKey} from "../apollo/query/ScheduledRecognition"
import {ScheduledGroupCelebrationData, scheduledGroupCelebrationsQuery} from "../apollo/query/ScheduledGroupCelebrations"
import {addItemToGroupCelebrationsFeed} from "../apollo/helpers/feed"
import {FeedData, feedQuery} from "../apollo/query/Feed"
import getRecognitionQuery from "./queries/Recognition"
import getRecognitionWithTeamsQuery from "./queries/RecognitionWithTeams"
import createRecognitionMutation from "./mutations/CreateRecognition"
import updatePollWithRewardIdMutation from "./mutations/UpdatePollWithReward"
import updateRecognitionWithRewardIdMutation from "./mutations/UpdateRecognitionWithRewardId"
import updateRecognitionWithRewardMutation from "./mutations/UpdateRecognitionWithReward"
import createScheduledRecognitionMutation from "./mutations/CreateScheduledRecognition"
import createGroupCelebrationMutation, {createGroupCelebrationMutationKey} from "./mutations/CreateGroupCelebration"
import createGroupCelebrationMessageMutation, {createGroupCelebrationMessageMutationKey} from "./mutations/CreateGroupCelebrationMessage"
import addReactionMutation, {addReactionMutationKey} from "./mutations/AddReaction"
import toggleReactionMutation, {toggleReactionMutationKey} from "./mutations/ToggleReaction"
import deleteReactionMutation from "./mutations/DeleteReaction"
import updateScheduledRecognitionMutation, {updateScheduledRecognitionKey} from "./mutations/UpdateScheduledRecognition"
import deleteScheduledRecognitionMutation from "./mutations/DeleteScheduledRecognition"
import deleteGroupCelebrationMutation from "./mutations/DeleteGroupCelebration"
import recognitionErrorHandler from "./errors/recognitionErrorHandler"
import updateGroupCelebrationMessageMutation, {updateGroupCelebrationMessageMutationKey} from "./mutations/UpdateGroupCelebrationMessage"
import updateGroupCelebrationMutation, {updateGroupCelebrationMutationKey} from "./mutations/UpdateGroupCelebration"
import {LIKE_REACTION} from "@irati/recognition/enums/Reactions"

interface RecognitionPayload {
  message: string
  source: RecognitionSource
  walletTransferId?: string
  teamIds?: string[]
  userIds?: string[]
  expectReward?: boolean
  isPrivate?: boolean
  companyValues?: string[]
  occasionId?: string
}
interface MyGroupCelebrationsFeed {
  accountGroupCelebrations: GroupCelebrationList
}
class RecognitionService extends PBService {
  get(id: string, args?: QueryOptions): Promise<Recognition> {
    return client.query({query: getRecognitionQuery, variables: {id}, ...args}).then(({data}) => {
      return data.recognition
    })
  }

  getWithTeams(id: string, args?: QueryOptions): Promise<Recognition> {
    return client.query({query: getRecognitionWithTeamsQuery, variables: {id}, ...args}).then(({data}) => {
      return data.recognition
    })
  }

  create(recognition: Partial<RecognitionPayload>): Promise<Recognition> {
    return client.mutate({mutation: createRecognitionMutation, variables: {recognition}}).then(({data}) => {
      return data.createRecognition
    })
  }

  updateRecognitionWithReward(recognitionId: string, walletTransferId: string): Promise<Recognition> {
    return client.mutate({mutation: updateRecognitionWithRewardMutation, variables: {recognitionId, walletTransferId}}).then(({data}) => {
      return data.updateRecognitionWithReward
    })
  }

  updateRecognitionWithRewardId(recognitionId: string, orderId: string): Promise<Recognition> {
    return client.mutate({mutation: updateRecognitionWithRewardIdMutation, variables: {recognitionId, orderId}}).then(({data}) => {
      return data.updateRecognitionWithRewardId
    })
  }

  updatePollWithRewardId(pollId: string, orderId: string): Promise<Poll> {
    return client.mutate({mutation: updatePollWithRewardIdMutation, variables: {pollId, orderId}}).then(({data}) => {
      return data.updatePollWithRewardId
    })
  }

  createScheduledRecognition(args: MutationCreateScheduledRecognitionArgs) {
    const {recognition} = args
    return client
      .mutate({
        mutation: createScheduledRecognitionMutation,
        variables: {scheduledRecognition: recognition},
        refetchQueries: [scheduledRecognitionsQuery]
      })
      .then(({data}) => {
        return data.createScheduledRecognition
      })
  }

  deleteScheduledRecognition(args: MutationDeleteScheduledRecognitionArgs) {
    const {uuid} = args
    return client
      .mutate({mutation: deleteScheduledRecognitionMutation, variables: {uuid}, refetchQueries: [scheduledRecognitionsQuery]})
      .then(({data}) => {
        return data.deleteScheduledRecognition
      })
  }

  getScheduledRecognitions() {
    return client.query({query: getScheduledRecognitionsQuery, fetchPolicy: "network-only"}).then(({data}) => {
      return data.accountScheduledRecognitions
    })
  }

  getScheduledRecognition(uuid: string) {
    return client.query({query: scheduledRecognitionQuery, variables: {id: uuid}, fetchPolicy: "network-only"}).then(({data}) => {
      return data[scheduledRecognitionQueryKey]
    })
  }

  updateScheduledRecognition(args: MutationUpdateScheduledRecognitionArgs) {
    const {recognition} = args
    return client.mutate({mutation: updateScheduledRecognitionMutation, variables: {scheduledRecognition: recognition}}).then(({data}) => {
      return data[updateScheduledRecognitionKey]
    })
  }

  addReaction(parentId: string, reaction: ReactionInput) {
    const variables: MutationAddReactionArgs = {
      reaction: {
        parentId,
        reaction
      }
    }
    return client.mutate({mutation: addReactionMutation, variables}).then(({data}) => {
      return data[addReactionMutationKey]
    })
  }

  deleteReaction(uuid: string) {
    return client.mutate({mutation: deleteReactionMutation, variables: {uuid}})
  }

  async toggleLike(entity: Recognition | Poll | RecognitionFeedComment | GroupCelebration, type: FeedEntityType) {
    const variables = {
      reaction: {
        parentId: entity.uuid,
        reaction: LIKE_REACTION,
        type
      }
    }
    const newReactions = [...entity.reactions?.results!]
    const likeReactionIndex = newReactions.findIndex(
      (reaction) => reaction.emoji?.code === LIKE_REACTION.code && authentication.user().uuid === reaction.createdBy?.uuid
    )
    if (likeReactionIndex >= 0) {
      newReactions.splice(likeReactionIndex, 1)
    } else {
      newReactions.push({
        uuid: "optimistic-" + new Date().getTime(),
        emoji: {...LIKE_REACTION, __typename: "Emoji"},
        createdBy: authentication.user(),
        __typename: "RecognitionFeedReaction"
      })
    }
    const defaultResponse = {
      [FeedEntityType.Recognition]: null,
      [FeedEntityType.Poll]: null,
      [FeedEntityType.Comment]: null
    }
    const optimisticResponse = {
      [toggleReactionMutationKey]: {
        ...defaultResponse,
        [type]: {
          ...entity,
          reactions: {
            ...entity.reactions,
            results: newReactions
          }
        },
        __typename: "ToggleReactionResponse"
      }
    }
    return client
      .mutate({
        mutation: toggleReactionMutation,
        variables,
        optimisticResponse
      })
      .then(({data}) => {
        return data[toggleReactionMutationKey]
      })
  }
  async attachReward(destinationUserIds: string[], pointsPerUser: number, recognition: Recognition) {
    const wallet = await walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward)

    const variables = {
      sourceId: wallet.uuid!,
      destinationUserIds,
      amount: pointsPerUser * 100,
      message: recognition.message!
    }
    const totalAmount = pointsPerUser * destinationUserIds.length
    if (wallet.availableBalance! < totalAmount) {
      return
    }
    const {transferIds} = await flexiPointService.rewardEmployees(variables)
    const {status} = await flexiPointService.getTransferOutcome(transferIds[0])
    if (status !== "COMPLETE") {
      throw Error("Transfer failed")
    }
    return this.updateRecognitionWithReward(recognition.uuid!, transferIds[0])
  }
  createGroupCelebration(args: MutationCreateGroupCelebrationArgs) {
    const {scheduledGroupCelebration} = args
    return client
      .mutate({
        mutation: createGroupCelebrationMutation,
        variables: {scheduledGroupCelebration},
        update: (cache: DataProxy, {data: {createGroupCelebration}}) => {
          const query = {...scheduledGroupCelebrationsQuery}
          let result: MyGroupCelebrationsFeed | null
          try {
            result = cache.readQuery(query)
          } catch (e) {
            result = null
          }
          if (result) {
            const {accountGroupCelebrations} = result
            const newFeed = addItemToGroupCelebrationsFeed(accountGroupCelebrations, createGroupCelebration)

            cache.writeQuery({
              ...query,
              data: {
                accountGroupCelebrations: newFeed
              }
            })
          }
        }
      })
      .then(({data}) => {
        return data[createGroupCelebrationMutationKey]
      })
  }

  createGroupCelebrationMessage(args: MutationCreateGroupCelebrationMessageArgs) {
    return client.mutate({mutation: createGroupCelebrationMessageMutation, variables: args}).then(({data}) => {
      return data[createGroupCelebrationMessageMutationKey]
    })
  }

  updateGroupCelebrationMessage(args: MutationUpdateGroupCelebrationMessageArgs) {
    return client.mutate({mutation: updateGroupCelebrationMessageMutation, variables: args}).then(({data}) => {
      return data[updateGroupCelebrationMessageMutationKey]
    })
  }

  updateGroupCelebration(input: UpdateGroupCelebrationInput) {
    return client.mutate({mutation: updateGroupCelebrationMutation, variables: {input}}).then(({data}) => {
      return data[updateGroupCelebrationMutationKey]
    })
  }

  deleteGroupCelebration(groupCelebration: GroupCelebration) {
    return client.mutate({
      mutation: deleteGroupCelebrationMutation,
      variables: {uuid: groupCelebration.uuid},
      refetchQueries: groupCelebration.status === GroupCelebrationStatus.Scheduled ? [scheduledGroupCelebrationsQuery] : [],
      update: (cache: DataProxy) => {
        if (groupCelebration.status !== GroupCelebrationStatus.Active) {
          return
        }
        const query = {...feedQuery}
        let result: FeedData | null
        try {
          result = cache.readQuery(query)
        } catch (e) {
          result = null
        }
        if (result) {
          const {results: oldFeed} = result.recognitionFeed
          const newFeed = oldFeed.filter((feedItem) => feedItem.uuid !== groupCelebration.uuid)
          cache.writeQuery({
            ...query,
            data: {
              recognitionFeed: {
                ...result.recognitionFeed,
                results: newFeed
              }
            }
          })
        }
      }
    })
  }

  removeGroupCelebrationFromCache(groupCelebration: GroupCelebration) {
    const query = {...scheduledGroupCelebrationsQuery}
    let result: ScheduledGroupCelebrationData["data"] | null
    try {
      result = client.cache.readQuery(query)
    } catch (e) {
      result = null
    }
    if (result) {
      const {results: oldFeed} = result.accountGroupCelebrations
      const newFeed = oldFeed.filter((feedItem) => feedItem.uuid !== groupCelebration.uuid)
      client.cache.writeQuery({
        ...query,
        data: {
          accountGroupCelebrations: {
            ...result.accountGroupCelebrations,
            results: newFeed
          }
        }
      })
    }
  }
}

const service = new RecognitionService({errorHandlers: [recognitionErrorHandler, rewardErrorHandler]})

export default service
